import { ButtonV2 } from '@synoptic/ui-kit/button-v2/button-v2.js';
import { Button } from '@synoptic/ui-kit/button/button.tsx';
import { EmptyState } from '@synoptic/ui-kit/empty-state/empty-state.tsx';
import { LockIcon } from '@synoptic/ui-kit/icons/react/lock.js';
import { MagnetEnergyIcon } from '@synoptic/ui-kit/icons/react/magnet-energy.tsx';
import { useIsTablet } from '@synoptic/ui-kit/responsive/hooks.ts';
import { type ReactNode } from 'react';
import { useSearchParams } from 'react-router';
import {
  CustomTargetsDataFragmentFragment,
  PackageInStreamFragmentFragment,
  StreamPostCardFragmentFragment,
  StripePriceFragmentFragment,
} from '@/__generated__/graphql';
import { StreamSubscription } from '@/__generated__/schema-types.ts';
import { LayoutCard } from '@/components/layout/card';
import { PolymorphicPostCard } from '@/components/polymorphic-post-card';
import { TargetsModal } from '@/components/streams/custom-stream/targets-modal.tsx';
import { SubscribePaidStreamButton } from '@/components/streams/toggle-paid-stream-subscription/subscribe-paid-stream-button';
import { useRedesign } from '@/feature-flags/redesign/use-redesign.ts';
import { followStreamButton } from '@/routes/_app.p.streams_.$id/stream-banner/stream-banner.css.ts';
import { useFollowStream } from '@/routes/_app.p.streams_.$id/use-toggle-follow-stream.ts';
import { useMe } from '@/routes/_app/use-me.ts';
import { mockStreamPost } from './mock-stream-post';
import {
  lockedPreviewOverlayV2,
  lockedPreviewRowV2,
  lockedPreviewV2,
} from './stream-data-v2.css.ts';
import { StreamEventList, StreamEventRowV2 } from './stream-event-row-v2.tsx';
import {
  lockedCustomStreamCtaPostAction,
  lockedStreamCtaPost,
  lockedStreamCtaPostAction,
} from './page.css';

const lockedPreviewEvent = {
  id: 'locked-preview',
  text: mockStreamPost.text ?? '',
  createdAt: mockStreamPost.createdAt ?? '',
  authorId: 'locked-preview',
  media: [],
};

const LockedPreviewV2 = ({
  icon,
  label,
  action,
}: {
  icon: ReactNode;
  label: string;
  action: ReactNode;
}) => (
  <div className={lockedPreviewV2}>
    <div className={lockedPreviewRowV2} inert>
      <StreamEventList>
        <div>
          <StreamEventRowV2 post={lockedPreviewEvent} />
        </div>
      </StreamEventList>
    </div>
    <EmptyState
      className={lockedPreviewOverlayV2}
      illustration={icon}
      heading={label}
      action={action}
    />
  </div>
);

export const LockedStreamCtaPost = ({
  streamId,
  inPackages,
  isPaid,
  prices,
  hasRights,
  showFollowButton,
}: {
  streamId: string;
  inPackages?: PackageInStreamFragmentFragment[];
  isPaid?: boolean;
  prices?: StripePriceFragmentFragment[];
  hasRights: boolean;
  showFollowButton?: boolean;
}) => {
  const redesign = useRedesign();
  const [searchParams] = useSearchParams();

  const paymentSuccess = searchParams.get('success') === 'true';

  const label =
    isPaid && !hasRights
      ? 'Latest posts will be unlocked after you subscribe'
      : 'Posts will be unlocked after you follow';

  const action =
    isPaid && !hasRights ? (
      <SubscribePaidStreamButton
        streamId={streamId}
        loading={paymentSuccess}
        packages={inPackages}
        prices={prices}
        popoverAlign="center"
      />
    ) : showFollowButton ? (
      <FollowStreamCtaButton streamId={streamId} />
    ) : null;

  if (redesign) {
    return (
      <LockedPreviewV2
        icon={<MagnetEnergyIcon width={40} height={40} />}
        label={label}
        action={action}
      />
    );
  }

  return (
    <div>
      <LayoutCard className={lockedStreamCtaPost}>
        <PolymorphicPostCard
          post={mockStreamPost as StreamPostCardFragmentFragment}
        />
        <div className={lockedStreamCtaPostAction}>
          <LockIcon width={40} height={40} />
          {label}
          {action}
        </div>
      </LayoutCard>
    </div>
  );
};

const FollowStreamCtaButton = ({ streamId }: { streamId: string }) => {
  const redesign = useRedesign();
  const isTablet = useIsTablet();
  const [followStream, { loading }] = useFollowStream(streamId);

  if (redesign) {
    return (
      <ButtonV2
        type="button"
        variant="primary"
        disabled={loading}
        onClick={followStream}
      >
        Follow
      </ButtonV2>
    );
  }

  return (
    <Button
      onClick={followStream}
      variant={'primary'}
      size={isTablet ? 'medium' : 'small'}
      className={followStreamButton}
      loading={loading}
    >
      Follow
    </Button>
  );
};

export const LockedCustomStreamCtaPost = ({
  streamId,
  customTargetsData,
  accessAllowed,
  prices,
  authorUsername,
  purchasedTargetsNumber,
  subscription,
}: {
  streamId: string;
  customTargetsData: NonNullable<CustomTargetsDataFragmentFragment>;
  purchasedTargetsNumber: number;
  accessAllowed: boolean;
  authorUsername: string;
  prices?: StripePriceFragmentFragment[];
  subscription?: StreamSubscription | null;
}) => {
  const redesign = useRedesign();
  const [searchParams] = useSearchParams();
  const me = useMe();

  const paymentSuccess = searchParams.get('success') === 'true';

  const isAuthor = me.username === authorUsername;

  const title = isAuthor
    ? `The buyers have not provided targets yet.`
    : accessAllowed
      ? 'You can request to monitor targets'
      : `You will be able to request to monitor targets after ${prices?.length ? 'purchase' : 'follow'}`;

  const action = isAuthor ? null : accessAllowed ? (
    <TargetsModal
      instruction={customTargetsData.targetTypeInstruction}
      maxTargets={purchasedTargetsNumber}
      trigger={
        redesign ? (
          <ButtonV2 type="button" variant="primary">
            Provide targets
          </ButtonV2>
        ) : (
          <Button>Provide targets</Button>
        )
      }
      targetValidationRegex={customTargetsData.targetValidationRegex}
      customTargetsData={customTargetsData}
      subscription={subscription}
    />
  ) : prices?.length ? (
    <SubscribePaidStreamButton
      streamId={streamId}
      loading={paymentSuccess}
      popoverAlign="center"
      customTargets={customTargetsData}
      prices={prices}
    />
  ) : null;

  if (redesign) {
    return (
      <LockedPreviewV2
        icon={<MagnetEnergyIcon width={40} height={40} />}
        label={title}
        action={action}
      />
    );
  }

  return (
    <div>
      <LayoutCard className={lockedStreamCtaPost}>
        <PolymorphicPostCard
          post={mockStreamPost as StreamPostCardFragmentFragment}
        />
        <div className={lockedCustomStreamCtaPostAction}>
          <MagnetEnergyIcon width={40} height={40} />
          {title}
          {action}
        </div>
      </LayoutCard>
    </div>
  );
};
